import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMSPDashboard } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    Building2, Monitor, AlertTriangle, Shield, ArrowRight, Plus
} from 'lucide-react';

export const MSPDashboardPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            const result = await getMSPDashboard();
            setData(result);
        } catch (err) {
            toast.error('Failed to fetch MSP dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#10B981';
        if (score >= 60) return '#F59E0B';
        return '#EF4444';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="msp-dashboard-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const clients = data?.clients || [];
    const totalDevices = data?.total_devices || 0;
    const openAlerts = data?.open_alerts || 0;
    const avgScore = data?.average_security_score || 0;

    return (
        <div className="space-y-8" data-testid="msp-dashboard-page">
            {/* Page Header */}
            <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
                    MSP Dashboard
                </h1>
                <p className="text-muted-foreground mt-1">
                    Overview of all client organizations
                </p>
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="border-border shadow-sm card-hover">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">TOTAL CLIENTS</CardTitle>
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="font-heading text-4xl font-bold">{clients.length}</div>
                    </CardContent>
                </Card>

                <Card className="border-border shadow-sm card-hover">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">TOTAL DEVICES</CardTitle>
                        <Monitor className="w-4 h-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="font-heading text-4xl font-bold">{totalDevices}</div>
                    </CardContent>
                </Card>

                <Card className="border-border shadow-sm card-hover">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">OPEN ALERTS</CardTitle>
                        <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="font-heading text-4xl font-bold">{openAlerts}</div>
                    </CardContent>
                </Card>

                <Card className="border-border shadow-sm card-hover">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">AVG SECURITY SCORE</CardTitle>
                        <Shield className="w-4 h-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="font-heading text-4xl font-bold" style={{ color: getScoreColor(avgScore) }}>
                            {avgScore}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Client Health Grid */}
            {clients.length === 0 ? (
                <Card className="border-border shadow-sm">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-lg font-medium text-foreground mb-2">No clients yet</p>
                        <p className="text-sm text-muted-foreground mb-4">Add your first client to get started</p>
                        <Button onClick={() => navigate('/msp/clients')}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Client
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div>
                    <h2 className="font-heading text-lg font-semibold mb-4">Client Health</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {clients.map((client) => {
                            const score = client.security_score || 0;
                            const scoreColor = getScoreColor(score);
                            return (
                                <Card key={client.id} className="border-border shadow-sm card-hover">
                                    <CardContent className="pt-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="font-medium text-foreground">{client.name}</h3>
                                                {client.industry && (
                                                    <Badge variant="secondary" className="mt-1 text-xs">
                                                        {client.industry}
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex-shrink-0">
                                                <svg width="48" height="48" viewBox="0 0 48 48">
                                                    <circle cx="24" cy="24" r="20" fill="none" stroke="#E2E8F0" strokeWidth="4" />
                                                    <circle
                                                        cx="24" cy="24" r="20" fill="none"
                                                        stroke={scoreColor} strokeWidth="4" strokeLinecap="round"
                                                        strokeDasharray={`${score * 1.256} 125.6`}
                                                        transform="rotate(-90 24 24)"
                                                    />
                                                    <text x="24" y="28" textAnchor="middle" className="text-xs font-bold" fill={scoreColor}>
                                                        {score}
                                                    </text>
                                                </svg>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                                            <div className="flex items-center gap-1.5">
                                                <Monitor className="w-3.5 h-3.5" />
                                                {client.device_count || 0} devices
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="status-dot status-online"></span>
                                                {client.online_devices || 0}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="status-dot status-offline"></span>
                                                {client.offline_devices || 0}
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="w-3.5 h-3.5 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">{client.alert_count || 0} alerts</span>
                                                {(client.critical_alerts || 0) > 0 && (
                                                    <Badge variant="destructive" className="text-xs rounded-full">
                                                        {client.critical_alerts} critical
                                                    </Badge>
                                                )}
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-sm text-primary"
                                                onClick={() => navigate(`/msp/clients/${client.id}`)}
                                            >
                                                View <ArrowRight className="w-4 h-4 ml-1" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MSPDashboardPage;
